import React from 'react'
import { Link } from 'react-router-dom'
import {
  Box,
  Snackbar,
  TextField,
  Button,
  ListItem,
  List,
  CircularProgress,
  Paper,
  Typography,
  Grid,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
} from '@mui/material'
import MuiAlert from '@mui/material/Alert'
import API from '@aws-amplify/api'
import { useDispatch, useSelector } from 'react-redux'
import { IReduxState } from 'interfaces/IReduxState'
import { IProduction, Production } from 'interfaces/IProduction'
import { STOCK_CARD_TYPE_HALF_COMPLETE } from 'enums/stockCard'
import { IBatch } from 'interfaces/IPremix'
import { Alert } from '@mui/material';
import LoadPackagingAutocomplete from 'shared/autocomplete/loadPackagingAutocomplete'
import LoadPremixAutocomplete from 'shared/autocomplete/loadPremixAutocomplete'
import LoadSalesOrderAutocomplete from 'shared/autocomplete/loadSalesOrderAutocomplete'
import UnitsSelect from 'shared/autocomplete/unitsSelect'
import { IPackaging } from 'interfaces/IPackaging'

export default function AddProductionHalfComplete() {
  const type = STOCK_CARD_TYPE_HALF_COMPLETE
  const [productionData, setProductionData] = React.useState<IProduction>(
    new Production({})
  )
  const [isFailureSnackbarOpen, setIsFailureSnackbarOpen] =
    React.useState(false)
  const [isSuccessSnackbarOpen, setIsSuccessSnackbarOpen] =
    React.useState(false)
  const [isLoading, setIsLoading] = React.useState<any>(false)
  const [alertMessage, setAlertMessage] = React.useState('')
  const [batches, setBatches] = React.useState<IBatch[]>([])
  const [premixDepartment, setPremixDepartment] = React.useState<string>(
    'kosmetik'
  )
  const [factoryType, setFactoryType] = React.useState<string>('lws')
  const [currentPackaging, setCurrentPackaging] = React.useState<IPackaging | null>(null)

  const user = useSelector((state: IReduxState) => state.currentUser || {})

  const handleProductionBatchFieldChange = React.useCallback((
    value: any,
    key: string,
    batchNumber: string
  ): void => {
    setProductionData((prevProductionData) => {
      const productionDataBatches = [...prevProductionData.batches]
      const matchingBatch = productionDataBatches.find(
        (batch) => batch.batchNumber === batchNumber
      )
      if (matchingBatch) {
        // @ts-ignore
        matchingBatch[key] = value
      } else {
        const batch = batches.find((batch) => batch.batchNumber === batchNumber)
        if (batch) {
          productionDataBatches.push({
            ...(batch as any),
            // @ts-ignore
            [key]: value,
          } as any)
        }
      }
      return {
        ...prevProductionData,
        batches: productionDataBatches,
      }
    })
  }, [batches])

  React.useEffect(() => {
    if (!productionData?.batches?.length) return

    productionData.batches.forEach((batch) => {
      if (!batch.fillingUnit) {
        handleProductionBatchFieldChange(
          premixDepartment?.toLowerCase() === 'ot' ? 'PCS' : 'PCS',
          'fillingUnit',
          batch.batchNumber
        )
      }
    })
  }, [productionData.batches, premixDepartment, handleProductionBatchFieldChange])

  const handleSnackbarClose = (): void => {
    setIsFailureSnackbarOpen(false)
    setIsSuccessSnackbarOpen(false)
  }

  const handleProductionFieldChange = (value: any, key: string): void => {
    setProductionData((prev) => ({ ...prev, [key]: value }))
  }

  const saveProduction = (e: React.SyntheticEvent): void => {
    e.preventDefault()
    setIsLoading(true)
    const displayType = 'Setengah Jadi'

    // If packagingId is provided, ensure packaging has parts before creating production
    const ensurePackagingParts = async () => {
      if (!productionData.packagingId) return true
      try {
        const packaging = await API.get(
          'products',
          `/packaging/getPackaging/${productionData.packagingId}`,
          {}
        )
        if (!packaging || packaging.length === 0) {
          setAlertMessage(
            `Gagal menambah permintaan ${displayType}: Kemasan tidak ditemukan atau tidak aktif.`
          )
          setIsFailureSnackbarOpen(true)
          return false
        }
        const parts = packaging[0].packagingParts || []
        if (parts.length === 0) {
          setAlertMessage(
            `Gagal menambah permintaan ${displayType}: Kemasan tidak memiliki bagian.`
          )
          setIsFailureSnackbarOpen(true)
          return false
        }
        return true
      } catch (err) {
        setAlertMessage(
          `Gagal memeriksa kemasan: ${productionData.packagingId}`
        )
        setIsFailureSnackbarOpen(true)
        console.error((err as any)?.response || err)
        return false
      }
    }

    ensurePackagingParts().then((ok) => {
      if (!ok) {
        setIsLoading(false)
        return
      }

      // Filter batches: only include those that are fully filled.
      const allBatches = productionData.batches || []
      const filteredBatches = allBatches.filter((batch) => {
        const hasAll = [
          batch.quantity,
          batch.packagingAmount,
          batch.fillingAmount,
          batch.fillingUnit,
        ].every((v) => v !== undefined && v !== null && v !== '')
        const isCurrentPremixBatch = batches.some(
          (b) => b.batchNumber === batch.batchNumber
        )
        return hasAll && isCurrentPremixBatch
      })

      API.post('products', `/production/createProduction`, {
        body: { ...productionData, type, batches: filteredBatches },
      })
        .then((response) => {
          const partiallyFilled = allBatches.filter((batch) => {
            const hasAny = [
              batch.quantity,
              batch.packagingAmount,
              batch.fillingAmount,
              batch.fillingUnit,
            ].some((v) => v !== undefined && v !== null && v !== '')
            const hasAll = [
              batch.quantity,
              batch.packagingAmount,
              batch.fillingAmount,
              batch.fillingUnit,
            ].every((v) => v !== undefined && v !== null && v !== '')
            return hasAny && !hasAll
          })

          if (partiallyFilled.length > 0) {
            setAlertMessage(
              `Berhasil menambah permintaan ${displayType}. Namun ${partiallyFilled.length} batch tidak dikirim karena tidak lengkap.`
            )
          } else {
            setAlertMessage(`Berhasil menambah permintaan ${displayType}`)
          }

          setIsSuccessSnackbarOpen(true)
          setProductionData(new Production({}))
        })
        .catch((error) => {
          const resp = (error as any)?.response
          if (resp && resp.data && resp.data.error) {
            setAlertMessage(
              `Gagal menambah permintaan ${displayType}: ${resp.data.error}`
            )
          } else {
            setAlertMessage(`Gagal menambah permintaan ${displayType}.`)
          }

          setIsFailureSnackbarOpen(true)
          console.error(resp || error)
        })
        .finally(() => {
          setIsLoading(false)
          window.location.reload()
        })
    })
  }

  const getBatches = async (premixId: string) => {
    const response = await API.get('products', '/premix/getBatches', {
      queryStringParameters: { premixId, onlyAvailable: true },
    })
    setBatches(response?.batches || response?.data || response || [])
  }

  React.useEffect(() => {
    if (productionData.premixId) {
      getBatches(productionData.premixId)
      // Clear any previously entered batch values when premix changes
      setProductionData((prev) =>
        prev.batches?.length ? { ...prev, batches: [] } : prev
      )
    } else {
      setProductionData((prev) =>
        prev.batches?.length ? { ...prev, batches: [] } : prev
      )
    }
  }, [productionData.premixId])

  const dispatch = useDispatch()
  React.useEffect(() => {
    dispatch({ type: 'SET_PAGE_TITLE', payload: 'Buat Permintaan Bikin STJ' })
  }, [dispatch])

  const handleProductionBatchQuantityChange = (
    batchNumber: string,
    quantity: string
  ) => {
    const productionDataBatches = [...productionData.batches]
    const matchingBatch = productionDataBatches.find(
      (batch) => batch.batchNumber === batchNumber
    )
    if (matchingBatch) {
      // @ts-ignore
      matchingBatch.quantity = quantity
    } else {
      const batch = batches.find((batch) => batch.batchNumber === batchNumber)
      if (batch) {
        productionDataBatches.push({
          ...(batch as any),
          // @ts-ignore
          quantity,
        } as any)
      }
    }
    setProductionData({
      ...productionData,
      batches: productionDataBatches,
    })
  }

  function validateBatchRows() {
    const relevantBatches = Array.isArray(batches) ? batches : []
    const productionBatches = productionData.batches || []

    let atLeastOneFullyFilled = false
    const invalidRows: string[] = []

    relevantBatches.forEach((batch) => {
      const pb: any =
        productionBatches.find((p) => p.batchNumber === batch.batchNumber) || {}

      const q = Number(pb.quantity ?? 0)
      const pkg = Number(pb.packagingAmount ?? 0)
      const fill = Number(pb.fillingAmount ?? 0)
      const unit = pb.fillingUnit ?? ''

      const anyFilled = q > 0 || pkg > 0 || fill > 0 || (unit && unit !== '')
      const allFilled = q > 0 && pkg > 0 && fill > 0 && unit && unit !== ''

      if (anyFilled && !allFilled) invalidRows.push(batch.batchNumber)
      if (allFilled) atLeastOneFullyFilled = true
    })

    return { atLeastOneFullyFilled, invalidRows }
  }

  const getValidationErrors = () => {
    const errors: string[] = []

    // HALF COMPLETE CHECKS
    if (!productionData.premixId) errors.push('Ruahan wajib dipilih')
    if (!productionData.packagingId) errors.push('Kemasan wajib dipilih')

    const { atLeastOneFullyFilled, invalidRows } = validateBatchRows()
    const batchHasData = productionData.batches.some((batch) => batch.quantity)

    if (!getDoesBatchExist())
      errors.push(`Tidak ada bets untuk ${productionData.premixId}`)
    if (!batchHasData) errors.push('Belum ada data bets yang diisi')
    if (!atLeastOneFullyFilled)
      errors.push(
        'Setidaknya satu baris harus diisi lengkap (Qty Ruahan, Qty Packaging, Filling Amount, Unit)'
      )
    if (invalidRows.length > 0)
      errors.push(`Baris bets berikut belum lengkap: ${invalidRows.join(', ')}`)
    if (productionData.packagingId && !isAllPackagingEnough())
      errors.push('Total Kemasan Tidak Boleh Lebih dari Stok Kemasan Lama')

    return errors
  }

  const isAllFieldsComplete = () => {
    return getValidationErrors().length === 0
  }
  const getTotalPackagingRequested = () => {
    return productionData.batches.reduce(
      (acc, batch) => acc + Number(batch.packagingAmount || 0),
      0
    )
  }

  const isAllPackagingEnough = () => {
    // If no packaging is selected, we don't block based on sufficiency
    if (!productionData.packagingId) return true

    if (currentPackaging) {
      const availablePackagingAmount = Number(currentPackaging.stock ?? 0)
      return getTotalPackagingRequested() <= availablePackagingAmount
    } else {
      // If packaging is selected but currentPackaging state is not yet set
      return false
    }
  }

  const isBatchQuantityMoreThanRemaining = (batch: IBatch) => {
    const matchingBatch = productionData.batches.find(
      (productionDataBatch) =>
        batch.batchNumber === productionDataBatch.batchNumber
    )
    return (
      Number(matchingBatch?.quantity ?? 0) > Number(batch.currentQuantity ?? 0)
    )
  }

  const getDoesBatchExist = () => {
    return Boolean(
      productionData.premixId && Array.isArray(batches) && batches.length
    )
  }

  const batchValidation = validateBatchRows()

  return (
    <Box>
      <Box mb={2}>
        <Button
          component={Link}
          to="/production/manageProductionFormHalfComplete"
          variant="outlined"
          color="primary"
        >
          pergi ke Daftar Permintaan Bikin STJ
        </Button>
      </Box>
      <Box>
        <Paper>
          <List>
            <>
              <ListItem>
                <LoadPackagingAutocomplete
                  name={productionData.packagingId}
                  onChange={(e) => {
                    setCurrentPackaging(e)
                    handleProductionFieldChange(
                      e?.packagingId ?? '',
                      'packagingId'
                    )
                  }}
                />
              </ListItem>
              <ListItem>
                <Typography>
                  Stok Kemasan Lama:{' '}
                  {currentPackaging
                    ? Number(currentPackaging.stock ?? 0).toFixed(0)
                    : '--- '}
                  pcs
                </Typography>
              </ListItem>
              <ListItem>
                <LoadPremixAutocomplete
                  name={productionData.premixId}
                  onChange={(e) => {
                    handleProductionFieldChange(e?.premixId ?? '', 'premixId')
                    setPremixDepartment(e?.department ?? 'kosmetik')
                    setFactoryType(e?.department?.toLowerCase() === 'ot' ? 'ot' : 'lws')
                    // Also clear all batch fields when premix changes
                  }}
                />
              </ListItem>
              <ListItem>
                <FormControl component="fieldset">
                  <FormLabel component="legend">Factory Type</FormLabel>
                  <RadioGroup
                    row
                    value={factoryType}
                    onChange={(e) => setFactoryType(e.target.value)}
                  >
                    <FormControlLabel
                      value="ot"
                      control={<Radio color="primary" />}
                      label="OT"
                    />
                    <FormControlLabel
                      value="lws"
                      control={<Radio color="primary" />}
                      label="Kosmetik"
                    />
                  </RadioGroup>
                </FormControl>
              </ListItem>
              {productionData.premixId && !getDoesBatchExist() && (
                <ListItem>
                  <Alert severity="error">
                    Tidak ada bets untuk {productionData.premixId}
                  </Alert>
                </ListItem>
              )}
              {Array.isArray(batches) &&
                batches.map((batch) => (
                  <>
                    <ListItem>
                      <Grid container alignItems="center">
                        <Grid item xs={2}>
                          <Typography>No. Bets: {batch.batchNumber}</Typography>
                        </Grid>
                        <Grid item xs={2}>
                          <Typography>
                            Sisa: {Number(batch.currentQuantity ?? 0).toFixed(6)}
                          </Typography>
                        </Grid>
                        <Grid item xs={2}>
                          <TextField
                            required
                            fullWidth
                            autoComplete="off"
                            variant="outlined"
                            error={isBatchQuantityMoreThanRemaining(batch)}
                            helperText={
                              isBatchQuantityMoreThanRemaining(batch)
                                ? 'Quantity diminta tidak boleh lebih dari stok ruahan'
                                : ''
                            }
                            type="number"
                            label={`Qty Ruahan Requested (${premixDepartment?.toLowerCase() === 'ot' ? 'kapsul' : 'pcs'
                              })`}
                            value={
                              productionData.batches.find(
                                (productionDataBatch) =>
                                  batch.batchNumber ===
                                  productionDataBatch.batchNumber
                              )?.quantity ?? ''
                            }
                            onChange={(e) =>
                              handleProductionBatchQuantityChange(
                                batch.batchNumber,
                                e.target.value
                              )
                            }
                          />
                        </Grid>
                        <Grid item xs={2}>
                          <TextField
                            required
                            autoComplete="off"
                            variant="outlined"
                            fullWidth
                            type="number"
                            label="Qty Packaging Requested"
                            value={
                              productionData.batches.find(
                                (productionDataBatch) =>
                                  batch.batchNumber ===
                                  productionDataBatch.batchNumber
                              )?.packagingAmount ?? ''
                            }
                            onChange={(e) =>
                              handleProductionBatchFieldChange(
                                e.target.value,
                                'packagingAmount',
                                batch.batchNumber
                              )
                            }
                            error={batchValidation.invalidRows.includes(
                              batch.batchNumber
                            )}
                            helperText={
                              batchValidation.invalidRows.includes(
                                batch.batchNumber
                              )
                                ? 'Baris belum lengkap'
                                : ''
                            }
                          />
                        </Grid>
                        <Grid item xs={2}>
                          <TextField
                            required
                            autoComplete="off"
                            fullWidth
                            variant="outlined"
                            type="number"
                            label="Filling Amount Requested"
                            value={
                              productionData.batches.find(
                                (productionDataBatch) =>
                                  batch.batchNumber ===
                                  productionDataBatch.batchNumber
                              )?.fillingAmount ?? ''
                            }
                            onChange={(e) =>
                              handleProductionBatchFieldChange(
                                e.target.value,
                                'fillingAmount',
                                batch.batchNumber
                              )
                            }
                            error={batchValidation.invalidRows.includes(
                              batch.batchNumber
                            )}
                            helperText={
                              batchValidation.invalidRows.includes(
                                batch.batchNumber
                              )
                                ? 'Baris belum lengkap'
                                : ''
                            }
                          />
                        </Grid>
                        <Grid item xs={2}>
                          <UnitsSelect
                            // @ts-ignore
                            value={
                              productionData.batches.find(
                                (productionDataBatch) =>
                                  batch.batchNumber ===
                                  productionDataBatch.batchNumber
                              )?.fillingUnit ??
                              (premixDepartment?.toLowerCase() === 'ot' ? 'PCS' : 'PCS')
                            }
                            options={['KG', 'PCS']}
                            onChange={(e) =>
                              handleProductionBatchFieldChange(
                                e.target.value,
                                'fillingUnit',
                                batch.batchNumber
                              )
                            }
                          />
                          {batchValidation.invalidRows.includes(
                            batch.batchNumber
                          ) && (
                              <Typography color="error" variant="caption">
                                Baris belum lengkap
                              </Typography>
                            )}
                        </Grid>
                      </Grid>
                    </ListItem>
                  </>
                ))}

              <ListItem>
                <LoadSalesOrderAutocomplete
                  label={
                    factoryType?.toLowerCase() === 'ot'
                      ? 'No. SO (26/SO-OT/XXX)'
                      : 'No. SO (26/SO-LWS/XXX)'
                  }
                  factoryType={factoryType}
                  onChange={(e) => {
                    if (e && e.salesOrderId) {
                      handleProductionFieldChange(
                        e.salesOrderId,
                        'salesOrderId'
                      )
                    } else {
                      handleProductionFieldChange('', 'salesOrderId')
                    }
                  }}
                />
              </ListItem>
            </>
            <ListItem>
              <TextField
                fullWidth
                label="Notes"
                value={productionData.notes}
                onChange={(e) =>
                  handleProductionFieldChange(e.target.value, 'notes')
                }
              />
            </ListItem>
            <ListItem>
              <Typography>Requested By: {user.username}</Typography>
            </ListItem>
          </List>
        </Paper>
        {isLoading && (
          <Box p={3}>
            <CircularProgress />
          </Box>
        )}
        <React.Fragment>
          <Box mt={3} />
        </React.Fragment>
        <Paper>
          <Box p={2} mt={3}>
            {!isAllPackagingEnough() && (
              <Alert severity="error">
                Total Kemasan Tidak Boleh Lebih dari Stok Kemasan Lama
              </Alert>
            )}
            {batchValidation.invalidRows.length > 0 && (
              <Alert severity="error">
                Baris berikut belum lengkap:{' '}
                {batchValidation.invalidRows.join(', ')}. Jika mengisi salah
                satu field pada baris, semua field harus diisi.
              </Alert>
            )}
            {!batchValidation.atLeastOneFullyFilled && getDoesBatchExist() && (
              <Alert severity="error">
                Setidaknya satu baris harus diisi lengkap (Qty Ruahan, Qty
                Packaging, Filling Amount, Unit).
              </Alert>
            )}
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={!isAllFieldsComplete() || isLoading}
              onClick={saveProduction}
            >
              Save
            </Button>
            {!isAllFieldsComplete() && (
              <Box mt={1}>
                {getValidationErrors().map((err) => (
                  <Typography
                    key={err}
                    variant="caption"
                    color="error"
                    display="block"
                  >
                    • {err}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>
        </Paper>
      </Box>
      <React.Fragment>
        <Box mt={3} />
      </React.Fragment>
      <Snackbar
        open={isSuccessSnackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <MuiAlert severity="success" elevation={6} variant="filled">
          {alertMessage}
        </MuiAlert>
      </Snackbar>
      <Snackbar
        open={isFailureSnackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <MuiAlert severity="error" elevation={6} variant="filled">
          {alertMessage}
        </MuiAlert>
      </Snackbar>
    </Box>
  )
}
